"use client";

import { Text, Flex, Box, TextField, Link } from "@radix-ui/themes";
import OtpInput from "react-otp-input";
import React, { useEffect, useState } from "react";
import { CountryList, DEFAULT_COUNTRY_CODE } from "../config/constants";
import { isValidOTP, isValidPassword } from "../services/validationService";
import { allowNumberOnly } from "../services/utilService";
import CountryCodeView from "../common/countryCodeView";
import { Country } from "../services/modelService";
import ResendCounter from "../common/resendCounter";
import { MixPanelService } from "../services/mixpanelService";
import AlertMessage from "../common/alertMessage";

export type PhoneNUmberCredential = {
  phone: string;
  otp: string;
  countryCode: string;
  isValidPhoneNumber: boolean;
  isValidOTP: boolean;
};

type PhoneLoginProps = {
  enableOTP: boolean;
  isSubmitted: boolean;
  credentialData: PhoneNUmberCredential | null;
  handleCredentialData: any;
  onEnableEmailView: () => void;
  resendCounter: boolean;
  onResendOTP: () => void;
};

const PhoneNumberLogin: React.FC<PhoneLoginProps> = (props) => {
  const [otp, setOTP] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<any>(
    (props && props.credentialData && props.credentialData.countryCode) ||
      CountryList.find((country) => country.code === DEFAULT_COUNTRY_CODE)
  );
  const [enablePasswordView, setPassWordView] = useState(false);

  const handlePaste = (e: any) => {
    const pastedText = e.clipboardData.getData("text/plain");
    const filteredText = pastedText.replace(/[^0-9]/g, "");
    props.handleCredentialData({
      phone: filteredText,
      otp: props.credentialData?.otp,
      countryCode: selectedCountry.code,
      isValidOTP: isValidOTP(
        props.credentialData ? props.credentialData.otp : ""
      ),
      isValidPhoneNumber: isValidPassword(filteredText),
    });
  };

  useEffect(() => {
    setPassWordView(props.enableOTP);
    if (props && props.enableOTP) MixPanelService.track("otp_screen");
  }, [props]);

  useEffect(() => {
    props.handleCredentialData({
      ...props.credentialData,
      otp: otp,
      isValidOTP: isValidOTP(otp || ""),
    });
  }, [otp]);

  useEffect(() => {
    props.handleCredentialData({
      ...props.credentialData,
      countryCode: selectedCountry.code,
    });
  }, [selectedCountry.code]);

  return (
    <>
      {!enablePasswordView && (
        <label>
          <Text className="label-name">Phone Number</Text>
          <Flex className="flex-col">
            <Flex gap="2">
              <Box className="countryDropdown !b-none bg-white rounded">
                <CountryCodeView
                  selectedCountryCode={selectedCountry?.code}
                  isEnable={true}
                  isFilter={false}
                  handleSelection={(country: Country) => {
                    setSelectedCountry(country);
                  }}
                />
              </Box>

              <Box width="100%">
                <TextField.Root
                  className={`${
                    !props.credentialData?.isValidPhoneNumber &&
                    props.isSubmitted
                      ? "input-invalid"
                      : "input-valid"
                  } text-sm`}
                  autoFocus
                  size="3"
                  defaultValue=""
                  maxLength={50}
                  placeholder="Enter your phone number"
                  onKeyDown={allowNumberOnly}
                  onPaste={handlePaste}
                  onChange={(e) =>
                    props.handleCredentialData({
                      ...props.credentialData,
                      phone: e.target.value,
                      isValidPhoneNumber: isValidPassword(e.target.value),
                    })
                  }
                  value={props.credentialData?.phone}
                  aria-label="Phone number"
                  role="textbox"
                />
              </Box>
            </Flex>
            {!props.credentialData?.isValidPhoneNumber && props.isSubmitted && (
              <AlertMessage message="Please enter valid phone number" />
            )}
          </Flex>

          <Flex justify="start" mt="2">
            <Link
              size={{ initial: "1", xl: "2" }}
              underline="hover"
              onClick={() => props.onEnableEmailView()}
              aria-label="Switch to email login"
              role="link"
            >
              <Flex className="items-center" direction={"row"} gap="1">
                <Text className="font-semibold text-sm text-primary-default">
                  Use Email Address
                </Text>
              </Flex>
            </Link>
          </Flex>
        </label>
      )}
      {enablePasswordView && (
        <label>
          <Flex gap="2" className="justify-center items-center">
            <Box onKeyDown={allowNumberOnly}>
              <OtpInput
                value={otp}
                onChange={setOTP}
                shouldAutoFocus
                numInputs={6}
                renderSeparator={<span className="p-2" />}
                renderInput={(propData: any) => (
                  <input
                    autoFocus
                    {...propData}
                    style={{
                      border:
                        !props.credentialData?.isValidOTP && props.isSubmitted
                          ? "1px solid #EF6868"
                          : "1px solid gray",
                      borderRadius: "15%",
                      width: "40px",
                      height: "40px",

                      textAlign: "center",
                    }}
                    aria-label={`OTP digit ${propData.key + 1}`}
                    role="textbox"
                  />
                )}
              />
              {!props.credentialData?.isValidOTP && props.isSubmitted && (
                <AlertMessage message="Please enter valid OTP" />
              )}
            </Box>
          </Flex>

          <ResendCounter
            enableCounter={props && props.resendCounter}
            handleResend={() => props.onResendOTP()}
          />
        </label>
      )}
    </>
  );
};

export default PhoneNumberLogin;
